import Api from '../Api';
import checkAddOrganizationPayload from './checkAddOrganizationPayload';
import { buildAllCitiesSelection, isAllCitiesSelection } from './allCitiesOfCountry';
import {
  normalizeContactDigits,
  organizationFormDays,
  scheduleFromWorkTimePartial,
  stripJsonFromPastedText,
} from './organizationAutofill';
import { normalizePhotoUrls } from './photoUrls';

export const DAY_KEYS = organizationFormDays.map((day) => day.value);

export const NON_HOUR_DAY_TYPES = ['day_off', 'temp_closed', 'whole_day'];

export const CONTACT_FIELDS = [
  { key: 'phone_number', label: 'Телефон', digitsOnly: true },
  { key: 'instagram', label: 'Instagram', digitsOnly: false },
  { key: 'telegram', label: 'Telegram', digitsOnly: false },
  { key: 'whatsapp', label: 'WhatsApp', digitsOnly: true },
  { key: 'viber', label: 'Viber', digitsOnly: true },
  { key: 'facebook', label: 'Facebook', digitsOnly: false },
  { key: 'website', label: 'Сайт', digitsOnly: false },
];

export const OFFER_STATUSES = {
  draft: 'draft',
  creating: 'creating',
  created: 'created',
  error: 'error',
};

/** Режимы городов, когда страна одна и та же для всех предложений */
export const CITY_MODES = {
  perOffer: 'perOffer',
  single: 'single',
  allCitiesOfCountry: 'allCitiesOfCountry',
};

/** «У всех разное» / «одно и то же для всех» — для страны, категории, подкатегории */
export const SELECTION_MODES = {
  all: 'all',
  single: 'single',
};

export function buildInitialBulkConfig() {
  return {
    ownerId: '',
    countryMode: SELECTION_MODES.all,
    country: null,
    cityMode: CITY_MODES.perOffer,
    city: null,
    categoryMode: SELECTION_MODES.all,
    category: null,
    subCategoryMode: SELECTION_MODES.all,
    subCategory: null,
    templatePhotoIds: [],
    photosPerOffer: 1,
    photoUrls: [],
  };
}

/**
 * Настройки экрана → значения, которые реально применяются ко всем предложениям.
 * Незавершённые выборы (режим «одна», но ничего не выбрано) считаются отсутствующими.
 */
export function normalizeBulkConfig(config) {
  const country = config.countryMode === SELECTION_MODES.single && config.country?._id ? config.country : null;
  const category = config.categoryMode === SELECTION_MODES.single && config.category?._id ? config.category : null;
  const subCategory = category && config.subCategoryMode === SELECTION_MODES.single && config.subCategory?._id
    ? config.subCategory
    : null;

  const cityMode = country && !category?.is_online ? config.cityMode : CITY_MODES.perOffer;
  const city = cityMode === CITY_MODES.single && config.city?._id ? config.city : null;

  return {
    ...config,
    country,
    category,
    subCategory,
    cityMode,
    city,
    templatePhotoIds: config.templatePhotoIds || [],
    photosPerOffer: Math.max(1, Number(config.photosPerOffer) || 1),
    photoUrls: normalizePhotoUrls(config.photoUrls).slice(0, 15),
  };
}

/** Что мешает скачать файл: пустые обязательные выборы режима. */
export function getConfigIssues(config) {
  const issues = [];

  if (!isValidOwnerId(config.ownerId)) {
    issues.push('Укажите ID владельца — 24 символа (hex).');
  }
  if (config.countryMode === SELECTION_MODES.single && !config.country?._id) {
    issues.push('Выберите страну или переключите режим на «Разные страны».');
  }
  if (config.categoryMode === SELECTION_MODES.single && !config.category?._id) {
    issues.push('Выберите категорию или переключите режим на «Разные категории».');
  }
  if (config.categoryMode === SELECTION_MODES.single
    && config.category?.sub_categories?.length
    && config.subCategoryMode === SELECTION_MODES.single
    && !config.subCategory?._id) {
    issues.push('Выберите подкатегорию или переключите режим на «Разные подкатегории».');
  }
  if (config.countryMode === SELECTION_MODES.single
    && config.country?._id
    && !config.category?.is_online
    && config.cityMode === CITY_MODES.single
    && !config.city?._id) {
    issues.push('Выберите город или переключите режим города.');
  }

  return issues;
}

const HEX_24 = /^[a-f\d]{24}$/i;
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

export function emptySchedule() {
  return DAY_KEYS.reduce((acc, day) => ({ ...acc, [day]: { start: '', end: '', type: '' } }), {});
}

function makeUid() {
  return `offer_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/* ─────────────────────────── Справочники ─────────────────────────── */

export async function fetchCitiesOfCountry(countryId) {
  if (!countryId) return [];
  const { data } = await Api.getCities({ country_id: countryId });
  return data?.cities || [];
}

const citiesCache = {};

/** Города запрашиваются часто (карточки, выбор города у выбранных) — держим их в памяти экрана. */
export async function fetchCitiesOfCountryCached(countryId) {
  if (!countryId) return [];
  if (!citiesCache[countryId]) {
    citiesCache[countryId] = fetchCitiesOfCountry(countryId).catch((e) => {
      delete citiesCache[countryId];
      throw e;
    });
  }
  return citiesCache[countryId];
}

export async function fetchTemplatePhotos({ categoryId, subCategoryId }) {
  if (!categoryId) return [];
  const payload = { category_id: categoryId };
  if (subCategoryId) {
    payload.sub_category_id = subCategoryId;
  }
  const { data } = await Api.getTemplatePhotosPhotos(payload);
  return data?.templatePhotos || [];
}

/* ─────────────────── Принудительные (общие) значения ─────────────────── */

export function isOnlineConfig(config) {
  return Boolean(config.category?.is_online);
}

/** Нужен ли городу выбор от модели: страна одна, а город — у каждого свой */
export function shouldAskModelForCities(config) {
  return Boolean(config.country) && config.cityMode === CITY_MODES.perOffer && !isOnlineConfig(config);
}

function forcedCitySelection(config) {
  if (!config.country || isOnlineConfig(config)) return null;
  if (config.cityMode === CITY_MODES.allCitiesOfCountry) {
    return buildAllCitiesSelection(config.country);
  }
  if (config.cityMode === CITY_MODES.single && config.city?._id) {
    return { ...config.city, is_in_all_cities_of_country: false };
  }
  return null;
}

/* ─────────────────────────── Файл для ИИ ─────────────────────────── */

function categoryForReference(category) {
  return {
    _id: category._id,
    name: category.name,
    is_online: Boolean(category.is_online),
    sub_categories: (category.sub_categories || []).map((sub) => ({ _id: sub._id, name: sub.name })),
  };
}

function buildReferenceData(config, references) {
  const { categories, languages, cities } = references;

  const reference = {
    languages: (languages || []).map((lang) => ({ _id: lang._id, name: lang.name })),
  };

  if (config.category) {
    const fixedCategory = categoryForReference(config.category);
    if (config.subCategory?._id) {
      reference.fixed_category = {
        ...fixedCategory,
        sub_categories: fixedCategory.sub_categories.filter((sub) => sub._id === config.subCategory._id),
      };
      reference.fixed_sub_category = { _id: config.subCategory._id, name: config.subCategory.name };
    } else {
      reference.fixed_category = fixedCategory;
    }
  } else {
    reference.categories = (categories || []).map(categoryForReference);
  }

  if (config.country) {
    reference.fixed_country = { _id: config.country._id, name: config.country.name };
  }

  if (shouldAskModelForCities(config)) {
    reference.cities = (cities || []).map((city) => ({
      _id: city._id,
      name: city.name,
      country_id: city.country_id || config.country._id,
    }));
  } else if (config.cityMode === CITY_MODES.single && config.city?._id) {
    reference.fixed_city = { _id: config.city._id, name: config.city.name };
  }

  return reference;
}

function buildModeSummaryLines(config) {
  const lines = [];

  lines.push(`— Страна: ${config.country ? `одна для всех — ${config.country.name}` : 'разные (выбирается оператором для каждого предложения)'}`);

  if (isOnlineConfig(config)) {
    lines.push('— Город: не нужен, категория онлайн (предложение без физической точки)');
  } else if (!config.country) {
    lines.push('— Город: не указывается в JSON, оператор выберет его в админке для каждого предложения');
  } else if (config.cityMode === CITY_MODES.allCitiesOfCountry) {
    lines.push(`— Город: «Все города страны ${config.country.name}» для всех предложений (адрес и координаты не нужны)`);
  } else if (config.cityMode === CITY_MODES.single) {
    lines.push(`— Город: один для всех — ${config.city?.name}`);
  } else {
    lines.push(`— Город: у каждого предложения свой, выбирается из списка городов страны ${config.country.name} (REFERENCE_DATA.cities)`);
  }

  if (config.category) {
    lines.push(`— Категория: одна для всех — ${config.category.name}${config.category.is_online ? ' (онлайн)' : ''}`);
    if (config.subCategory?._id) {
      lines.push(`— Подкатегория: одна для всех — ${config.subCategory.name}`);
    } else if (config.category.sub_categories?.length) {
      lines.push('— Подкатегория: у каждого предложения своя, из REFERENCE_DATA.fixed_category.sub_categories');
    } else {
      lines.push('— Подкатегория: у этой категории подкатегорий нет');
    }
  } else {
    lines.push('— Категория: у каждого предложения своя, из REFERENCE_DATA.categories');
    lines.push('— Подкатегория: у каждого предложения своя, из sub_categories выбранной категории');
  }

  lines.push(`— ID владельца: ${config.ownerId} (проставляется админкой всем предложениям, в JSON не нужен)`);

  if (config.templatePhotoIds?.length) {
    lines.push(`— Фото: админка сама случайно раздаст по ${config.photosPerOffer} шаблонных фото каждому предложению; в JSON фото не указываются`);
  } else {
    lines.push('— Шаблонные фото: оператор выберет их в админке, если нужно; в JSON их не указывай');
  }

  if (config.photoUrls?.length) {
    lines.push(`— Фото по ссылке: админка проставит всем предложениям одни и те же ${config.photoUrls.length} ссылок; photo_urls в JSON не указывай`);
  } else {
    lines.push('— photo_urls: необязательный массив прямых http(s)-ссылок на картинки. Указывай только ссылки, которые реально есть в присланных данных. Не выдумывай URL. Если ссылок нет — поле не добавляй');
  }

  return lines;
}

function buildOfferShape(config) {
  const shape = {
    name: 'Название предложения',
    specialization: 'Короткая специализация (чем занимается)',
    description: 'Описание предложения обычным текстом',
    work_time: {
      monday: '09:00-18:00',
      tuesday: '09:00-18:00',
      wednesday: '09:00-18:00',
      thursday: '09:00-18:00',
      friday: '09:00-18:00',
      saturday: 'day_off',
      sunday: 'day_off',
    },
    contacts: {
      phone_number: '79991234567',
      instagram: 'nickname',
      website: 'https://example.com',
    },
    language_ids: ['<_id из REFERENCE_DATA.languages>'],
    tags: ['тег1', 'тег2'],
  };

  if (!config.category) {
    shape.category_id = '<_id из REFERENCE_DATA.categories>';
    shape.sub_category_ids = ['<_id из sub_categories выбранной категории>'];
  } else if (!config.subCategory?._id && config.category.sub_categories?.length) {
    shape.sub_category_ids = ['<_id из REFERENCE_DATA.fixed_category.sub_categories>'];
  }

  const needsAddress = !isOnlineConfig(config) && config.cityMode !== CITY_MODES.allCitiesOfCountry;

  if (shouldAskModelForCities(config)) {
    shape.city_id = '<_id из REFERENCE_DATA.cities>';
    shape.additional_city_ids = [];
  }

  if (needsAddress) {
    shape.address = 'Улица, дом';
    shape.coordinates = { lat: 40.177, lon: 44.503 };
  }

  return shape;
}

function buildRequiredFieldsLines(config) {
  const lines = [
    '— name, specialization, description — не короче 2 символов;',
    '— work_time — все 7 дней monday..sunday: либо "HH:MM-HH:MM", либо "day_off" / "temp_closed" / "whole_day";',
    '— contacts — хотя бы одно заполненное поле;',
    '— language_ids — минимум один _id из REFERENCE_DATA.languages;',
    '— tags — минимум один тег;',
  ];

  if (!config.category) {
    lines.push('— category_id — обязателен, только _id из REFERENCE_DATA.categories; если у выбранной категории есть sub_categories — обязателен sub_category_ids из этой же категории;');
  } else if (!config.subCategory?._id && config.category.sub_categories?.length) {
    lines.push('— sub_category_ids — минимум одна подкатегория из REFERENCE_DATA.fixed_category.sub_categories;');
  }

  if (shouldAskModelForCities(config)) {
    lines.push('— city_id — обязателен, только _id из REFERENCE_DATA.cities;');
  }

  if (!isOnlineConfig(config) && config.cityMode !== CITY_MODES.allCitiesOfCountry) {
    lines.push('— address и coordinates — обязательны для офлайн-предложений (координаты по адресу, максимально точно; оператор потом проверит их на карте).');
  }

  return lines;
}

/**
 * Текстовый файл для модели: инструкция + справочники + ожидаемый формат ответа.
 */
export function buildBulkOffersFileContent({ config, references }) {
  const referenceData = buildReferenceData(config, references);
  const offerShape = buildOfferShape(config);
  const expectedResponse = { offers: [offerShape] };

  const schemaHint = {
    description: 'Один JSON-объект с массивом offers: по одному объекту на каждое предложение, которое надо создать в админке Nomad.',
    root_shape: '{ "offers": [ ... ] }',
    expected_response_example: expectedResponse,
    forced_fields_note: 'Поля, зафиксированные оператором (см. РЕЖИМ РАБОТЫ), админка проставит сама. Не добавляй их в JSON: они будут перезаписаны.',
    json_syntax: 'Ответ одним сообщением: ТОЛЬКО блок ```json … ``` — внутри один валидный JSON, как после JSON.stringify, для JSON.parse. Никакого текста до ```json и после ```. Внутренние двойные кавычки в строках — только как \\".',
    rules: [
      'Каждому источнику (тексту/карточке/строке), который прислал оператор, соответствует ровно один объект в offers.',
      'Не выдумывай _id: каждый идентификатор должен буквально совпадать с _id из REFERENCE_DATA.',
      'Телефонные поля contacts.phone_number, contacts.whatsapp, contacts.viber — только цифры подряд, без +, скобок и дефисов.',
      'language_ids — только массив _id из REFERENCE_DATA.languages, человекочитаемые названия запрещены.',
      'tags — обычные строки по смыслу текста, не идентификаторы.',
      'Если данных для обязательного поля нет — выведи его из текста максимально правдоподобно, но не оставляй поле пустым.',
      'photo_urls — только реальные http(s)-ссылки из данных оператора. Не подставляй example.com и не генерируй картинки.',
    ],
  };

  const prompt = [
    'Ты — помощник администратора Nomad: помогаешь пакетно создавать предложения (организации) в админке.',
    '',
    '────────── Порядок работы ──────────',
    '1) Это первое сообщение — инструкция, режим работы и справочники. JSON на него генерировать НЕ надо.',
    '2) Ответь ровно одной фразой и больше ничем: «Задача ясна, присылай данные о предложениях».',
    '3) Дальше оператор будет присылать тексты, таблицы, файлы, ссылки — по одному или нескольким предложениям за сообщение. Каждое такое сообщение просто подтверждай коротко: «Принято, предложений в накоплении: N» (N — сколько предложений ты уже собрал). JSON не выводи.',
    '4) Когда оператор напишет «Собери файл» (или похожее — «собери всё», «сгенерируй JSON»), верни один блок ```json … ``` со всеми накопленными предложениями в массиве offers.',
    '5) Если после сборки оператор прислал ещё данные — снова накапливай их и по команде верни новый JSON только с новыми предложениями (если оператор прямо не попросил собрать весь список заново).',
    '',
    '────────── РЕЖИМ РАБОТЫ (задан оператором) ──────────',
    ...buildModeSummaryLines(config),
    '',
    'КРИТИЧНО: зафиксированные оператором значения применяются ко ВСЕМ предложениям принудительно, даже если в присланном тексте написано другое (другой город, другая категория и т.п.). Не пытайся их переопределить и не добавляй эти поля в JSON.',
    '',
    '────────── Обязательные поля каждого предложения ──────────',
    ...buildRequiredFieldsLines(config),
    '',
    '────────── КРИТИЧНО — формат ответа ──────────',
    '— Результат всегда только внутри блока кода с типом json:',
    '```json',
    '{ "offers": [ ... ] }',
    '```',
    '— Без текста до открывающего ```json и после закрывающих ```.',
    '— Внутри — один валидный JSON: без markdown, без комментариев, ключи в двойных кавычках, внутренние кавычки экранированы как \\".',
    '— Никаких переводов строк внутри строковых значений, если они ломают JSON.',
    '',
    '────────── ОЖИДАЕМЫЙ ФОРМАТ ОТВЕТА (заглушки заменить реальными данными) ──────────',
    JSON.stringify(expectedResponse, null, 2),
    '',
    '────────── Проверка перед отправкой ──────────',
    '1) offers — массив, в нём столько объектов, сколько предложений прислал оператор;',
    '2) все идентификаторы взяты буквально из REFERENCE_DATA;',
    '3) у каждого предложения заполнены все обязательные поля из списка выше;',
    '4) работает JSON.parse — кавычки и запятые на месте.',
    '',
    'REFERENCE_DATA:',
    JSON.stringify(referenceData, null, 2),
    '',
    'SCHEMA_HINT:',
    JSON.stringify(schemaHint, null, 2),
  ].join('\n');

  return [
    '══════════════════════════════════════════════════════════════',
    'NOMAD — массовое создание предложений (данные + промпт для ИИ)',
    '══════════════════════════════════════════════════════════════',
    '',
    'Как пользоваться (оператору):',
    '1) Отправьте языковой модели весь этот файл одним сообщением. Модель ответит «Задача ясна…».',
    '2) Присылайте модели данные о предложениях: тексты, выгрузки, ссылки — сколько угодно сообщений.',
    '3) Когда всё отправлено, напишите «Собери файл». Модель вернёт один блок ```json … ``` с массивом offers.',
    '4) Сохраните ответ модели в файл .json (или скопируйте текст) и загрузите на том же экране админки, где скачали этот файл.',
    '5) В админке появятся карточки предложений: можно поправить поля по одному или сразу у нескольких и создать выбранные.',
    '6) Если админка не принимает ответ из-за синтаксиса, откройте https://jsonviewer.stack.hu/, вставьте текст, нажмите «Format» и исправьте структуру.',
    '',
    prompt,
  ].join('\n');
}

/* ─────────────────────────── Разбор ответа ─────────────────────────── */

function toTrimmedString(value) {
  if (value === undefined || value === null) return '';
  return String(value).trim();
}

function normalizeScheduleValue(value) {
  if (value === undefined || value === null) return null;

  if (typeof value === 'string') {
    const clean = value.replace(/\s/g, '');
    if (clean.includes('-')) {
      const [start, end] = clean.split('-');
      if (TIME_RE.test(start) && TIME_RE.test(end)) {
        return { start, end, type: 'hours' };
      }
      return null;
    }
    if (NON_HOUR_DAY_TYPES.includes(clean)) {
      return { start: '', end: '', type: clean };
    }
    return null;
  }

  if (typeof value === 'object') {
    if (value.type === 'hours') {
      const start = toTrimmedString(value.start);
      const end = toTrimmedString(value.end);
      return TIME_RE.test(start) && TIME_RE.test(end) ? { start, end, type: 'hours' } : null;
    }
    if (NON_HOUR_DAY_TYPES.includes(value.type)) {
      return { start: '', end: '', type: value.type };
    }
  }

  return null;
}

function scheduleFromRawOffer(raw) {
  const schedule = emptySchedule();
  const source = raw.schedule && typeof raw.schedule === 'object'
    ? raw.schedule
    : scheduleFromWorkTimePartial(raw.work_time) || {};

  DAY_KEYS.forEach((day) => {
    const normalized = normalizeScheduleValue(source[day]);
    if (normalized) {
      schedule[day] = normalized;
    }
  });

  return schedule;
}

function contactsFromRawOffer(raw) {
  const contacts = {};
  if (!raw.contacts || typeof raw.contacts !== 'object') return contacts;

  CONTACT_FIELDS.forEach(({ key, digitsOnly }) => {
    const value = toTrimmedString(raw.contacts[key]);
    if (!value) return;
    contacts[key] = digitsOnly ? normalizeContactDigits(value) : value;
  });

  return contacts;
}

function findCategory(categories, categoryId) {
  if (!categoryId) return null;
  return (categories || []).find((category) => category._id === categoryId) || null;
}

function findCity(cities, cityId) {
  if (!cityId) return null;
  return (cities || []).find((city) => city._id === cityId) || null;
}

function coordinatesFromRawOffer(raw) {
  const source = raw.coordinates;
  if (!source || typeof source !== 'object') return null;

  const lat = toTrimmedString(source.lat ?? source.latitude);
  const lon = toTrimmedString(source.lon ?? source.lng ?? source.longitude);
  if (!lat && !lon) return null;

  return {
    lat,
    lon,
    is_special_point_on_map: Boolean(source.is_special_point_on_map),
  };
}

function pickRawOffers(data) {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== 'object') return null;

  const keys = ['offers', 'organizations', 'items', 'list'];
  for (let i = 0; i < keys.length; i += 1) {
    if (Array.isArray(data[keys[i]])) return data[keys[i]];
  }
  return null;
}

export function pickRandomIds(pool, count) {
  if (!pool.length) return [];
  const shuffled = [...pool];
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled.slice(0, Math.min(count, shuffled.length));
}

/**
 * Один объект из ответа модели → черновик карточки предложения.
 * Зафиксированные оператором поля перекрывают данные модели.
 */
function buildDraft({
  raw, config, references, index,
}) {
  const warnings = [];
  const { categories, languages, cities } = references;

  let category = config.category || null;
  if (!category) {
    const rawCategoryId = toTrimmedString(raw.category_id);
    category = findCategory(categories, rawCategoryId);
    if (rawCategoryId && !category) {
      warnings.push('категория из файла не найдена в справочнике — выберите её вручную');
    }
  }

  let subCategories = [];
  if (config.category && config.subCategory?._id) {
    subCategories = [config.subCategory];
  } else if (category?.sub_categories?.length) {
    const rawSubIds = Array.isArray(raw.sub_category_ids) ? raw.sub_category_ids.map(toTrimmedString) : [];
    subCategories = category.sub_categories.filter((sub) => rawSubIds.includes(sub._id));
    if (rawSubIds.length && !subCategories.length) {
      warnings.push('подкатегории из файла не найдены в выбранной категории');
    }
  }

  const isOnline = Boolean(category?.is_online);

  let city = null;
  let additionalCities = [];
  let address = '';
  let coordinates = null;

  if (!isOnline) {
    city = forcedCitySelection(config);

    if (!city && shouldAskModelForCities(config)) {
      const rawCityId = toTrimmedString(raw.city_id);
      city = findCity(cities, rawCityId);
      if (rawCityId && !city) {
        warnings.push('город из файла не найден в справочнике страны — выберите его вручную');
      }
      const rawAdditionalIds = Array.isArray(raw.additional_city_ids)
        ? raw.additional_city_ids.map(toTrimmedString)
        : [];
      additionalCities = rawAdditionalIds.map((id) => findCity(cities, id)).filter(Boolean);
    }

    if (!isAllCitiesSelection(city)) {
      address = toTrimmedString(raw.address);
      coordinates = coordinatesFromRawOffer(raw);
    } else {
      additionalCities = [];
    }
  }

  const rawLanguageIds = Array.isArray(raw.language_ids) ? raw.language_ids.map(toTrimmedString) : [];
  const offerLanguages = (languages || []).filter((lang) => rawLanguageIds.includes(lang._id));
  if (rawLanguageIds.length && !offerLanguages.length) {
    warnings.push('языки из файла не найдены в справочнике');
  }

  const tags = Array.isArray(raw.tags)
    ? raw.tags.map(toTrimmedString).filter(Boolean)
    : [];

  return {
    uid: makeUid(),
    sourceIndex: index,
    status: OFFER_STATUSES.draft,
    statusText: '',
    createdId: '',
    warnings,

    ownerId: toTrimmedString(config.ownerId),
    name: toTrimmedString(raw.name),
    specialization: toTrimmedString(raw.specialization),
    description: toTrimmedString(raw.description),
    category,
    subCategories,
    city,
    additionalCities,
    address,
    coordinates,
    schedule: scheduleFromRawOffer(raw),
    contacts: contactsFromRawOffer(raw),
    languages: offerLanguages,
    is_for_all_languages: offerLanguages.length === 0,
    tags,
    templatePhotoIds: pickRandomIds(config.templatePhotoIds || [], config.photosPerOffer || 1),
    photoUrls: photoUrlsForDraft(raw, config, warnings),
  };
}

function photoUrlsForDraft(raw, config, warnings) {
  const fromConfig = normalizePhotoUrls(config.photoUrls || []);
  const rawList = Array.isArray(raw.photo_urls) ? raw.photo_urls : [];
  const fromRaw = normalizePhotoUrls(rawList);
  const source = fromConfig.length ? fromConfig : fromRaw;

  if (!fromConfig.length && rawList.length && !fromRaw.length) {
    warnings.push('ссылки photo_urls из файла не похожи на http(s) — добавьте фото вручную');
  }
  if (source.length > 15) {
    warnings.push('ссылок на фото больше 15 — взяты первые 15');
  }
  return source.slice(0, 15);
}

export function parseBulkOffersJson(rawText, { config, references }) {
  let data;
  try {
    data = JSON.parse(stripJsonFromPastedText(rawText));
  } catch (e) {
    return { error: 'Не удалось разобрать JSON. Проверьте скобки и кавычки (можно выровнять текст на jsonviewer.stack.hu).' };
  }

  const rawOffers = pickRawOffers(data);
  if (!rawOffers) {
    return { error: 'Ожидается объект { "offers": [...] } или массив предложений.' };
  }
  if (!rawOffers.length) {
    return { error: 'Массив offers пуст — модель не прислала ни одного предложения.' };
  }

  const drafts = [];
  for (let i = 0; i < rawOffers.length; i += 1) {
    const raw = rawOffers[i];
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
      return { error: `Предложение ${i + 1}: ожидался объект с полями предложения.` };
    }
    drafts.push(buildDraft({
      raw, config, references, index: i,
    }));
  }

  return { drafts };
}

/* ─────────────────────── Валидация и создание ─────────────────────── */

export function scheduleToWorkTime(schedule) {
  return DAY_KEYS.reduce((acc, day) => {
    const value = schedule?.[day];
    if (!value?.type) return { ...acc, [day]: '' };
    if (value.type === 'hours') {
      return { ...acc, [day]: `${value.start}-${value.end}` };
    }
    return { ...acc, [day]: value.type };
  }, {});
}

function scheduleErrors(schedule) {
  const invalidDays = organizationFormDays.filter(({ value }) => {
    const day = schedule?.[value];
    if (!day?.type) return true;
    if (day.type === 'hours') return !TIME_RE.test(day.start || '') || !TIME_RE.test(day.end || '');
    return !NON_HOUR_DAY_TYPES.includes(day.type);
  });

  if (!invalidDays.length) return [];
  return [` * заполните график работы: ${invalidDays.map((day) => day.name.toLowerCase()).join(', ')}`];
}

/** Черновик → те же аргументы, что форма создания предложения отдаёт в проверку. */
function draftToPayloadArgs(draft) {
  const isInAllCities = isAllCitiesSelection(draft.city);

  return {
    owner_id: draft.ownerId || '',
    name: draft.name || '',
    category_id: draft.category?._id,
    sub_category_ids: draft.subCategories?.map((item) => item._id) || [],
    must_set_subcategories: Boolean(draft.category?.sub_categories?.length),
    country_id: draft.city?.country_id,
    city_id: isInAllCities ? undefined : draft.city?._id,
    additionalCities: isInAllCities ? [] : draft.additionalCities || [],
    specialization: draft.specialization || '',
    address: draft.address || '',
    coordinates: draft.coordinates,
    workTime: scheduleToWorkTime(draft.schedule),
    contacts: draft.contacts || {},
    description: draft.description || '',
    languages: draft.languages || [],
    is_for_all_languages: draft.is_for_all_languages !== undefined
      ? Boolean(draft.is_for_all_languages)
      : true,
    tags: draft.tags || [],
    photos: (draft.photoUrls || []).map((url) => ({ url })),
    templatePhotoIds: draft.templatePhotoIds || [],
    withPhotos: true,
    isOnlineWorking: Boolean(draft.category?.is_online),
    is_in_all_cities_of_country: isInAllCities,
  };
}

/**
 * Ошибки черновика теми же правилами, что и на экране создания предложения,
 * плюс строгая проверка графика (в форме её заполняет сам оператор).
 */
export function validateDraft(draft) {
  const { errors = [] } = checkAddOrganizationPayload(draftToPayloadArgs(draft));
  return [...errors, ...scheduleErrors(draft.schedule)];
}

/** FormData ровно того же вида, что отправляет форма создания одного предложения. */
export function buildDraftFormData(draft) {
  const { formData } = checkAddOrganizationPayload(draftToPayloadArgs(draft));
  return formData;
}

export function isValidOwnerId(ownerId) {
  return HEX_24.test(toTrimmedString(ownerId));
}
